const MS_PER_DAY = 1000 * 60 * 60 * 24
const COMPLETE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/
const INTEGER_PATTERN = /^\d+$/

export interface IsoDateResult {
  date: Date
  isoYear: number
  week: number
  weekday: number
}

export interface IsoRangeResult {
  isoYear: number
  week: number
  start: Date
  end: Date
}

export interface WeekConverterState {
  dateValue: string
  isoYear: string
  isoWeek: string
  dateBlurred: boolean
  isoYearBlurred: boolean
  isoWeekBlurred: boolean
}

export type WeekConverterAction =
  | { type: 'change-date'; value: string }
  | { type: 'change-iso-year'; value: string }
  | { type: 'change-iso-week'; value: string }
  | { type: 'blur-date' }
  | { type: 'blur-iso-year' }
  | { type: 'blur-iso-week' }
  | { type: 'use-today'; value: string }
  | { type: 'copy-date-to-iso'; result: IsoDateResult }
  | { type: 'copy-iso-to-date'; result: IsoRangeResult }

export interface IsoDateDerivation {
  result: IsoDateResult | null
  error: 'invalid_date' | null
}

export interface IsoRangeDerivation {
  result: IsoRangeResult | null
  error: 'invalid_iso_week' | null
}

export function weekInputDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function isoDateToInput(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const day = String(date.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseIsoInputDate(value: string): Date | null {
  const match = COMPLETE_DATE_PATTERN.exec(value)
  if (!match) return null

  const [yearValue, monthValue, dayValue] = value.split('-').map(Number)
  if (!yearValue || !monthValue || !dayValue) return null

  const date = new Date(Date.UTC(yearValue, monthValue - 1, dayValue))
  const isValid = date.getUTCFullYear() === yearValue &&
    date.getUTCMonth() === monthValue - 1 &&
    date.getUTCDate() === dayValue

  return isValid ? date : null
}

export function calculateIsoDate(date: Date): IsoDateResult {
  const normalized = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  ))
  const weekday = isoWeekday(normalized)
  normalized.setUTCDate(normalized.getUTCDate() + 4 - weekday)

  const isoYear = normalized.getUTCFullYear()
  const firstDay = new Date(Date.UTC(isoYear, 0, 1))
  const week = Math.ceil((((normalized.getTime() - firstDay.getTime()) / MS_PER_DAY) + 1) / 7)

  return {
    date,
    isoYear,
    week,
    weekday,
  }
}

export function calculateIsoRange(year: number, week: number): IsoRangeResult | null {
  if (!Number.isInteger(year) || !Number.isInteger(week)) return null
  if (year < 1 || year > 9999) return null

  const maxWeek = weeksInIsoYear(year)
  if (week < 1 || week > maxWeek) return null

  const janFourth = new Date(Date.UTC(year, 0, 4))
  const janFourthWeekday = isoWeekday(janFourth)
  const start = new Date(janFourth)
  start.setUTCDate(janFourth.getUTCDate() - janFourthWeekday + 1 + ((week - 1) * 7))

  const end = new Date(start)
  end.setUTCDate(start.getUTCDate() + 6)

  return {
    isoYear: year,
    week,
    start,
    end,
  }
}

export function createWeekConverterState(now: Date = new Date()): WeekConverterState {
  const dateValue = weekInputDate(now)
  const date = parseIsoInputDate(dateValue)
  if (!date) throw new Error('Unable to initialize ISO week converter date')
  const result = calculateIsoDate(date)

  return {
    dateValue,
    isoYear: String(result.isoYear),
    isoWeek: String(result.week),
    dateBlurred: false,
    isoYearBlurred: false,
    isoWeekBlurred: false,
  }
}

export function weekConverterReducer(
  state: WeekConverterState,
  action: WeekConverterAction,
): WeekConverterState {
  switch (action.type) {
    case 'change-date':
      return { ...state, dateValue: action.value }
    case 'change-iso-year':
      return { ...state, isoYear: action.value }
    case 'change-iso-week':
      return { ...state, isoWeek: action.value }
    case 'blur-date':
      return { ...state, dateBlurred: true }
    case 'blur-iso-year':
      return { ...state, isoYearBlurred: true }
    case 'blur-iso-week':
      return { ...state, isoWeekBlurred: true }
    case 'use-today':
      return {
        ...state,
        dateValue: action.value,
        dateBlurred: false,
      }
    case 'copy-date-to-iso':
      return {
        ...state,
        isoYear: String(action.result.isoYear),
        isoWeek: String(action.result.week),
        isoYearBlurred: false,
        isoWeekBlurred: false,
      }
    case 'copy-iso-to-date':
      return {
        ...state,
        dateValue: isoDateToInput(action.result.start),
        dateBlurred: false,
      }
  }
}

export function deriveDateToIso(value: string, blurred: boolean): IsoDateDerivation {
  const date = parseIsoInputDate(value)
  if (date) return { result: calculateIsoDate(date), error: null }

  return {
    result: null,
    error: blurred || COMPLETE_DATE_PATTERN.test(value) ? 'invalid_date' : null,
  }
}

export function deriveIsoToRange(
  isoYear: string,
  isoWeek: string,
  validation: Pick<WeekConverterState, 'isoYearBlurred' | 'isoWeekBlurred'>,
): IsoRangeDerivation {
  const complete = isoYear !== '' && isoWeek !== ''
  const validLexicalInput = INTEGER_PATTERN.test(isoYear) && INTEGER_PATTERN.test(isoWeek)
  const result = validLexicalInput
    ? calculateIsoRange(Number(isoYear), Number(isoWeek))
    : null

  if (result) return { result, error: null }

  return {
    result: null,
    error: complete || validation.isoYearBlurred || validation.isoWeekBlurred
      ? 'invalid_iso_week'
      : null,
  }
}

export function formatIsoDate(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

export function formatIsoWeekday(date: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    timeZone: 'UTC',
  }).format(date)
}

function isoWeekday(date: Date): number {
  const day = date.getUTCDay()
  return day === 0 ? 7 : day
}

function weeksInIsoYear(year: number): number {
  return calculateIsoDate(new Date(Date.UTC(year, 11, 28))).week
}
